import { useTranslator } from '@bluebeam/react-i18n';
import BasicBlueprint from 'components/Icons/BasicBlueprint';
import RFIOutline from 'components/Icons/RFIOutline';
import Submittal from 'components/Icons/Submittal';
import TapeRoll from 'components/Icons/TapeRoll';
import Users from 'components/Icons/Users';
import Checkbox from 'components/inputs/Checkbox';
import React, { useEffect, useState } from 'react';

export type FilesExport = {
  drawings: boolean;
  punch: boolean;
  rfis: boolean;
  submittals: boolean;
  users: boolean;
};

interface Props {
  selectedFiles: FilesExport;
  onChange(filesToExport: FilesExport): void;
  type: string;
}

const SelectFilesStep: React.FC<Props> = ({
  selectedFiles,
  onChange,
  type,
}) => {
  const { translate } = useTranslator(['requestDownloadModal']);
  const [filesToExport, setFilesToExport] = useState(selectedFiles);

  useEffect(() => {
    onChange(filesToExport);
  }, [filesToExport]);

  const onChangeDrawingData = (): void => {
    setFilesToExport({
      drawings: !filesToExport.drawings,
      punch: filesToExport.punch,
      rfis: filesToExport.rfis,
      submittals: filesToExport.submittals,
      users: filesToExport.users,
    });
  };

  const onChangePunchData = (): void => {
    setFilesToExport({
      drawings: filesToExport.drawings,
      punch: !filesToExport.punch,
      rfis: filesToExport.rfis,
      submittals: filesToExport.submittals,
      users: filesToExport.users,
    });
  };

  const onChangeRFIData = (): void => {
    setFilesToExport({
      drawings: filesToExport.drawings,
      punch: filesToExport.punch,
      rfis: !filesToExport.rfis,
      submittals: filesToExport.submittals,
      users: filesToExport.users,
    });
  };

  const onChangeSubmittalData = (): void => {
    setFilesToExport({
      drawings: filesToExport.drawings,
      punch: filesToExport.punch,
      rfis: filesToExport.rfis,
      submittals: !filesToExport.submittals,
      users: filesToExport.users,
    });
  };

  const onChangeUserData = (): void => {
    setFilesToExport({
      drawings: filesToExport.drawings,
      punch: filesToExport.punch,
      rfis: filesToExport.rfis,
      submittals: filesToExport.submittals,
      users: !filesToExport.users,
    });
  };

  const checkboxItems = [
    {
      id: 'export-drawing-data',
      titleText: translate(
        'requestDownloadModal:selectFilesStep.drawings.title'
      ),
      subText: translate(
        'requestDownloadModal:selectFilesStep.drawings.subText'
      ),
      onChange: onChangeDrawingData,
      checked: filesToExport.drawings,
      icon: (
        <BasicBlueprint
          width="18px"
          height="15px"
          style={{ color: '#4b575d' }}
        />
      ),
    },
    {
      id: 'export-punch-data',
      titleText: translate(
        'requestDownloadModal:selectFilesStep.punches.title'
      ),
      subText: translate(
        'requestDownloadModal:selectFilesStep.punches.subText'
      ),
      onChange: onChangePunchData,
      checked: filesToExport.punch,
      icon: (
        <TapeRoll width="16px" height="16px" style={{ color: '#4b575d' }} />
      ),
    },
    {
      id: 'export-rfi-data',
      titleText: translate('requestDownloadModal:selectFilesStep.rfis.title'),
      subText: translate('requestDownloadModal:selectFilesStep.rfis.subText'),
      onChange: onChangeRFIData,
      checked: filesToExport.rfis,
      icon: (
        <RFIOutline width="16px" height="15px" style={{ color: '#4b575d' }} />
      ),
    },
    {
      id: 'export-submittal-data',
      titleText: translate(
        'requestDownloadModal:selectFilesStep.submittals.title'
      ),
      subText: translate(
        'requestDownloadModal:selectFilesStep.submittals.subText'
      ),
      onChange: onChangeSubmittalData,
      checked: filesToExport.submittals,
      icon: (
        <Submittal width="15px" height="18px" style={{ fill: '#4b575d' }} />
      ),
    },
    {
      id: 'export-user-data',
      titleText: translate('requestDownloadModal:selectFilesStep.users.title'),
      subText: translate('requestDownloadModal:selectFilesStep.users.subText'),
      onChange: onChangeUserData,
      checked: filesToExport.users,
      icon: <Users width="16px" height="15px" style={{ color: '#4b575d' }} />,
    },
  ];

  return (
    <div className="px5 pt9 pb4 bb-3">
      {type === 'export' ? (
        <>
          <div className="mb2 medium font-size-7 color-gray-7">
            {translate(
              'requestDownloadModal:exportModal.selectFilesStep.title'
            )}
          </div>
          <div className="mb5 light font-size-5 color-gray-7">
            {translate(
              'requestDownloadModal:exportModal.selectFilesStep.content'
            )}
          </div>
        </>
      ) : (
        <>
          <div className="mb2 medium font-size-7 color-gray-7">
            {translate('requestDownloadModal:selectFilesStep.title')}
          </div>
          <div className="mb5 light font-size-5 color-gray-7">
            {translate('requestDownloadModal:selectFilesStep.content')}
          </div>
        </>
      )}
      <div className="b-3 bgd-light-grey-2 pb3">
        {checkboxItems.map((item) => {
          return (
            <div key={item.id} className="flex mt3">
              <div className="ml4 mr3" style={{ marginTop: '1px' }}>
                <Checkbox
                  id={item.id}
                  checked={item.checked}
                  onChange={item.onChange}
                />
              </div>
              <div className="flex justify-center mr2 hs2 ws2">
                <div>{item.icon}</div>
              </div>
              <div className="column">
                <div className="mb1 bold font-size-5 color-gray-7">
                  {item.titleText}
                </div>
                <div className="font-size-1 color-gray-7">{item.subText}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SelectFilesStep;
